// ABOUTME: SPEC-74 — the reel chrome's wordmark, and (when the tenant opts in) the button that starts the visit
// ABOUTME: over. With no `onReset` it renders the exact `<img>` the chrome always rendered — byte-identical.

import React from 'react';

/**
 * WHY one component for both compositions: the desktop wordmark lives in App.js and the mobile one in
 * MobileChrome, and both had the same inert `<img … pointer-events-none>`. The two callers keep their own
 * geometry (`className`, `style`) exactly as before; this only decides what element carries it.
 *
 * `onReset` absent (flag off, or a film / waitlist / checkout owns the screen) → the original image,
 * untouched, still not interactive. Present → a real `<button>`: focusable, Enter/Space activate it natively,
 * and it has an accessible name built from the brand name the caller already holds (from /api/config — no
 * brand literal here). The tap target is enlarged past the artwork by an invisible 8 px halo, because the
 * mobile wordmark is far narrower than a thumb.
 *
 * @param {{ src: string, alt: string, className: string, style: object, onReset?: Function }} props
 */
export default function LogoResetButton({ src, alt, className, style, onReset }) {
  if (!onReset) return <img src={src} alt={alt} className={className} style={style} />;
  const label = `${alt} — start over`;
  return (
    <button
      type="button"
      onClick={onReset}
      aria-label={label}
      title={label}
      data-testid="logo-reset"
      className="absolute left-1/2 -translate-x-1/2 block p-0 border-0 bg-transparent cursor-pointer rounded-sm transition-opacity hover:opacity-80 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/80"
      style={{ ...style, pointerEvents: 'auto' }}
    >
      <span aria-hidden="true" data-testid="logo-reset-halo" className="absolute -inset-2" />
      <img src={src} alt="" data-testid="logo-reset-image" draggable={false} className="block w-full object-contain pointer-events-none" />
    </button>
  );
}
